import { planQwenVoiceVisualAid } from './qwen-voice-visual-planner';
import { markerNameFromQwenVoiceVisualMarker } from './qwen-voice-visual-registry';

const IMAGE_MARKER_RE = /\[\[image:/i;
const VISUAL_SIGN_REQUEST_RE = /\b(cartelli?|segnali?|immagin[ei]|vedere|vederli|veder|mostra(?:re|rmi|mi|melo|meli|rli)?|mostrami|mostrameli|mostrarli|visuale)\b/i;
const VISUAL_FOLLOWUP_RE = /\b(quei|quelli|questi|mostrarli|mostrameli|vederli|parlamene|parlarne|parlarmene)\b/i;
const GENERIC_VISUAL_FOLLOWUP_RE = /\b(qualche|quei|quelli|questi|dei|delle|cartelli?|segnali?)\b/i;
const SPEED_LIMIT_RE = /\b(limiti?|velocit[àa]|autostrada|km\/?h|centotrenta|cento ?dieci|130|110)\b/i;
const NO_IMAGE_REQUEST_RE = /\b(senza immagini|senza foto|senza figure|senza cartelli|solo testo)\b/i;
const SPECIFIC_NON_SPEED_SIGN_RE = /\b(stop|precedenza|sosta|fermata|sorpasso|rotatoria|divieto di transito|senso vietato)\b/i;
const DEFENSIVE_NO_IMAGE_PATTERNS = [
  /\bnon\s+ho\s+(?:immagini|segnali|cartelli)\b/i,
  /\bnon\s+(?:posso|riesco)(?:\s+a)?\s+(?:mostrare|mostrarti|far(?:ti)?\s+(?:vedere|apparire)|visualizzare|caricare|trovare|vedere)[^.!?;:]{0,80}\b(?:immagini|segnali|cartelli)\b/i,
  /\bnon\s+(?:ci\s+sono|vedo|trovo)[^.!?;:]{0,60}\b(?:immagini|segnali|cartelli)\b/i,
  /\bnon\s+(?:ho\s+)?(?:abbastanza\s+)?contesto(?:\s+visivo)?\b/i,
  /\bsistema[^.!?;:]{0,80}\binviando[^.!?;:]{0,80}\b(?:immagini|segnali|cartelli|contesto)\b/i,
  /\bcerca(?:re)?[^.!?;:]{0,80}\b(?:immagini|segnali|cartelli)\b/i,
  /\bprogressi\b/i,
] as const;

const SPEED_LIMIT_VISUAL_IMAGE_MARKERS = [
  '[[image:sign:Limiti generali di velocità in Italia]]',
  '[[image:sign:Limite massimo di velocità — 80 km/h]]',
] as const;

// Founder decision D (2026-06-03): retired the canned speed-limit teaching facts.
// The grounded realtime Prof (P0 #3) now teaches the visible signs from their
// blessed meanings, so a neutral, fact-free fallback is all that's needed for the
// no-words edge case — it names no numbers/rules and cannot contradict the model.
const SPEED_LIMIT_VISUAL_TEACHING_PROSE =
  'Guardiamo insieme questi segnali: dimmi da quale vuoi partire e te lo spiego.';

export type QwenVoiceVisualAidKind =
  | 'speed-limits'
  | 'overtaking'
  | 'stopping-parking'
  | 'right-of-way'
  | 'warning'
  // P8: the deepened + new sign families must also surface proactively in a voice
  // call. resolveQwenVoiceVisualAid gates on this kind, so every planner topic needs
  // a kind or the sign is silently dropped (the bug this set fixes).
  | 'safety-distance'
  | 'mandatory'
  | 'prohibitions'
  | 'information'
  | 'traffic-lights'
  | 'road-markings'
  | 'temporary-signs';

export interface QwenVoiceVisualAid {
  kind: QwenVoiceVisualAidKind;
  transactionId?: string;
  topicId?: string;
  requestKind?: 'show_new' | 'reuse_current' | 'no_assets' | 'not_visual';
  qwenContextSummary?: string;
  markerNames: string[];
  message: string;
  prose: string;
}

interface VisibleImageLike {
  id: string;
}

export function buildSpeedLimitVisualTeachingAnswer(): string {
  return [
    ...SPEED_LIMIT_VISUAL_IMAGE_MARKERS,
    '',
    SPEED_LIMIT_VISUAL_TEACHING_PROSE,
  ].join('\n');
}

export function buildSpeedLimitVisualAidMessage(): string {
  return SPEED_LIMIT_VISUAL_IMAGE_MARKERS.join('\n');
}

export function buildSpeedLimitVisualTeachingProse(): string {
  return SPEED_LIMIT_VISUAL_TEACHING_PROSE;
}

function stripMarker(marker: string): string {
  return markerNameFromQwenVoiceVisualMarker(marker);
}

function buildVisualAid(
  kind: QwenVoiceVisualAidKind,
  markers: readonly string[],
  prose: string,
  metadata: Partial<Pick<QwenVoiceVisualAid, 'transactionId' | 'topicId' | 'requestKind' | 'qwenContextSummary'>> = {},
): QwenVoiceVisualAid {
  return {
    ...metadata,
    kind,
    markerNames: markers.map(stripMarker),
    message: markers.join('\n'),
    prose,
  };
}

function visualKindFromTopic(topicId: string): QwenVoiceVisualAidKind | null {
  switch (topicId) {
    case 'speed_limits': return 'speed-limits';
    case 'overtaking': return 'overtaking';
    case 'stopping_parking': return 'stopping-parking';
    case 'right_of_way': return 'right-of-way';
    case 'warning_danger': return 'warning';
    case 'safety_distance': return 'safety-distance';
    case 'mandatory': return 'mandatory';
    case 'prohibitions': return 'prohibitions';
    case 'information': return 'information';
    case 'traffic_lights': return 'traffic-lights';
    case 'road_markings': return 'road-markings';
    case 'temporary_signs': return 'temporary-signs';
    default: return null;
  }
}

export function resolveQwenVoiceVisualAid({
  userText,
  contextText = '',
  currentVisibleImages,
}: {
  userText: string;
  contextText?: string;
  currentVisibleImages?: VisibleImageLike[];
}): QwenVoiceVisualAid | null {
  const plan = planQwenVoiceVisualAid({ userText, contextText, currentVisibleImages });
  if (plan.requestKind !== 'show_new' && plan.requestKind !== 'reuse_current') return null;
  const kind = visualKindFromTopic(plan.topicId);
  if (!kind || !plan.visibleMessage) return null;
  return buildVisualAid(
    kind,
    plan.assets.map((asset) => asset.marker),
    plan.prose,
    {
      transactionId: plan.transactionId,
      topicId: plan.topicId,
      requestKind: plan.requestKind,
      qwenContextSummary: plan.qwenContextSummary,
    },
  );
}

export function isDefensiveNoImageAnswer(text: string): boolean {
  return DEFENSIVE_NO_IMAGE_PATTERNS.some((pattern) => pattern.test(text));
}

export function augmentQwenVoiceAssistantText({
  userText,
  contextText,
  assistantText,
  includeImages = true,
}: {
  userText: string;
  contextText?: string;
  assistantText: string;
  includeImages?: boolean;
}): string {
  const cleanAssistant = assistantText.trim();
  const cleanUser = userText.trim();
  const cleanContext = contextText?.trim() ?? '';
  if (!cleanAssistant || !cleanUser) return assistantText;
  if (IMAGE_MARKER_RE.test(cleanAssistant)) return assistantText;
  if (NO_IMAGE_REQUEST_RE.test(cleanUser)) return assistantText;

  if (!isSpeedLimitVisualRequest({ userText: cleanUser, contextText: cleanContext })) return assistantText;

  if (!isDefensiveNoImageAnswer(cleanAssistant)) {
    return includeImages
      ? [
          ...SPEED_LIMIT_VISUAL_IMAGE_MARKERS,
          '',
          cleanAssistant,
        ].join('\n')
      : cleanAssistant;
  }

  return includeImages ? buildSpeedLimitVisualTeachingAnswer() : buildSpeedLimitVisualTeachingProse();
}

export function isSpeedLimitVisualRequest({
  userText,
  contextText = '',
}: {
  userText: string;
  contextText?: string;
}): boolean {
  const cleanUser = userText.trim();
  if (!cleanUser || NO_IMAGE_REQUEST_RE.test(cleanUser)) return false;
  const userHasSpeedLimit = SPEED_LIMIT_RE.test(cleanUser);
  const contextHasSpeedLimit = SPEED_LIMIT_RE.test(contextText);
  if (!userHasSpeedLimit && SPECIFIC_NON_SPEED_SIGN_RE.test(cleanUser)) return false;
  const isVisualAsk = VISUAL_SIGN_REQUEST_RE.test(cleanUser) || (
    VISUAL_FOLLOWUP_RE.test(cleanUser) && VISUAL_SIGN_REQUEST_RE.test(contextText)
  );
  if (!isVisualAsk) return false;
  if (userHasSpeedLimit) return true;
  return contextHasSpeedLimit && (
    VISUAL_FOLLOWUP_RE.test(cleanUser) || GENERIC_VISUAL_FOLLOWUP_RE.test(cleanUser)
  );
}
